import { fetchAuthSession } from "./session";

/**
 * Checks if the current user is the tenant admin (Cognito group `admin` or `xhinola_admin`)
 * by decoding the JWT token and checking the cognito:groups claim. S-16: `business_admin`
 * is scoped to its own business, so it is not a tenant admin.
 * @returns Promise<boolean> - true if user is admin, false otherwise
 */
export const isUserAdmin = async (): Promise<boolean> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (!idToken) {
      return false;
    }

    // Decode the JWT token
    const tokenParts = idToken.split('.');
    if (tokenParts.length !== 3) {
      return false;
    }

    const tokenPayload = JSON.parse(atob(tokenParts[1]));
    
    const userGroups = tokenPayload['cognito:groups'] || [];
    return userGroups.includes('admin') || userGroups.includes('xhinola_admin');
  } catch (error) {
    console.error('Error checking user admin status:', error);
    return false;
  }
};

/**
 * Gets user groups from JWT token
 * @returns Promise<string[]> - Array of user groups
 */
export const getUserGroups = async (): Promise<string[]> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (!idToken) {
      return [];
    }

    const tokenParts = idToken.split('.');
    if (tokenParts.length !== 3) {
      return [];
    }

    const tokenPayload = JSON.parse(atob(tokenParts[1]));
    return tokenPayload['cognito:groups'] || [];
  } catch (error) {
    console.error('Error getting user groups:', error);
    return [];
  }
};

/**
 * Gets user information from JWT token
 * @returns Promise<object> - User information object
 */
export const getUserInfo = async (): Promise<{
  username?: string;
  email?: string;
  groups: string[];
  businessId?: string;
}> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (!idToken) {
      return { groups: [] };
    }

    const tokenParts = idToken.split('.');
    if (tokenParts.length !== 3) {
      return { groups: [] };
    }

    const tokenPayload = JSON.parse(atob(tokenParts[1]));
    
    return {
      username: tokenPayload['cognito:username'],
      email: tokenPayload['email'],
      groups: tokenPayload['cognito:groups'] || [],
      businessId: tokenPayload['custom:business_id'],
    };
  } catch (error) {
    console.error('Error getting user info:', error);
    return { groups: [] };
  }
}; 